import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Logo } from '@/components/common/Logo';
import { Check, X } from 'lucide-react';

interface PasswordRequirements {
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  hasMinLength: boolean;
  passwordsMatch: boolean;
}

export default function SetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenHash, setTokenHash] = useState('');
  const [type, setType] = useState('');

  const [requirements, setRequirements] = useState<PasswordRequirements>({
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
    passwordsMatch: false,
  });

  // Validate password against requirements
  const validatePassword = (pwd: string, confirmPwd: string) => {
    const specialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|<>?,./`~]/;

    setRequirements({
      hasLowercase: /[a-z]/.test(pwd),
      hasUppercase: /[A-Z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: specialChars.test(pwd),
      hasMinLength: pwd.length >= 8,
      passwordsMatch: pwd.length > 0 && pwd === confirmPwd,
    });
  };

  // Update validation when password or confirmPassword changes
  useEffect(() => {
    validatePassword(password, confirmPassword);
  }, [password, confirmPassword]);

  const isPasswordValid =
    requirements.hasLowercase &&
    requirements.hasUppercase &&
    requirements.hasNumber &&
    requirements.hasSpecialChar &&
    requirements.hasMinLength &&
    requirements.passwordsMatch;

  useEffect(() => {
    // Check URL hash first (Supabase default format: #access_token=...&type=invite)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const hashType = hashParams.get('type');

    // Also check query params (legacy custom email template format: ?token=...&type=...)
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const inviteType = params.get('type');

    // If we have access_token in hash, session is already established by Supabase
    // This happens when user clicks the invite email link
    if (accessToken && hashType) {
      // Session is established, user can now set their password
      // No need to verify token - Supabase already did that
      return;
    }

    // Otherwise, check for legacy token in query params
    if (token && inviteType) {
      setTokenHash(token);
      setType(inviteType);
      return;
    }

    // No valid token found
    toast({
      title: 'الرابط مامشيش صحيح',
      description: 'الرابط ديال إعداد كلمة السر ماصالحش أو صافي وقتو',
      variant: 'destructive',
    });
    navigate({ to: '/' });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast({
        title: 'غلط',
        description: 'تأكد أن جميع الشروط ديال كلمة السر محترمين',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Verify the token and establish session
      if (!tokenHash || !type) {
        throw new Error('معلومات الرابط ناقصة');
      }

      // Verify OTP token to establish session
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type as any,
      });

      if (verifyError) {
        console.error('Verify OTP error:', verifyError);
        throw new Error('رابط التأكيد غير صالح أو منتهي الصلاحية. الرجاء طلب رابط جديد');
      }

      if (!data.session) {
        throw new Error('فشل إنشاء الجلسة');
      }

      // Now update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error('Update password error:', updateError);
        throw updateError;
      }

      // User profile and restaurant are created automatically by database trigger
      // Sign out to force them to login with new password
      await supabase.auth.signOut();

      toast({
        title: 'نجحات',
        description: 'كلمة السر ديالك تحددات مزيان. دابا تقدر تدخل',
      });

      // Redirect to login page
      navigate({ to: '/' });
    } catch (error: any) {
      console.error('Error setting password:', error);
      toast({
        title: 'غلط',
        description: error.message || 'ماقدرناش نحطو كلمة السر. يمكن الرابط صافي وقتو',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground" />
      )}
      <span className={met ? 'text-green-500' : 'text-muted-foreground'}>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center">
          <Logo className="h-24 w-24 mb-4" />
          <CardTitle className="text-2xl">دير كلمة السر ديالك</CardTitle>
          <CardDescription>
            صاوب كلمة سر قوية للحساب ديالك
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                كلمة السر
              </label>
              <Input
                id="password"
                type="password"
                placeholder="دخل كلمة السر ديالك"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium">
                أكد كلمة السر
              </label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="أكد كلمة السر ديالك"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            {/* Password Requirements Checklist */}
            <div className="rounded-lg border bg-card p-4 space-y-2">
              <p className="text-sm font-medium mb-3">الشروط ديال كلمة السر:</p>
              <RequirementItem met={requirements.hasMinLength} text="على الأقل 8 حروف" />
              <RequirementItem met={requirements.hasLowercase} text="حرف صغير واحد (a-z)" />
              <RequirementItem met={requirements.hasUppercase} text="حرف كبير واحد (A-Z)" />
              <RequirementItem met={requirements.hasNumber} text="رقم واحد (0-9)" />
              <RequirementItem
                met={requirements.hasSpecialChar}
                text="رمز خاص واحد (!@#$%^&*...)"
              />
              <RequirementItem met={requirements.passwordsMatch} text="كلمات السر متطابقين" />
            </div>

            <Button type="submit" className="w-full" disabled={loading || !isPasswordValid}>
              {loading ? 'كنحط كلمة السر...' : 'تأكيد'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
